// Müşteri profili › "Müşteri yöneticileri" kartı (M16 T20, K13, Δ1; portal yetki v2 §5/§8.4):
// liste (rol + kapsam özeti) + "Yönetici davet et" (rol ZORUNLU — R2-15: firmada aktif Süper Admin
// yoksa varsayılan Süper Admin, varsa Admin; Admin'de kapsam + "kapsamıyla aynı set erişimi") +
// "Rol ve kapsam" düzenleme + kaldır (ConfirmDialog; son Süper Admin kalkacaksa uyarı — danışman
// kurala tabi değil). İzin: clients.edit_all | projects.invite_client | kpi.manage (backend assert'i
// ile aynı). Kullanıcı yoksa davet akışıyla yaratılır (mail), varsa yalnız `client_admins` satırı.

import { useEffect, useState } from "react";
import {
  CLIENT_ADMIN_ROLE_LABELS,
  type ClientAdminRow,
  type ClientAdminSet,
  type ClientEntity,
} from "@mahir/shared";
import {
  grantClientAdmin,
  listClientAdmins,
  listClientKpiUsers,
  revokeClientAdmin,
  updateClientAdmin,
} from "../../api/client-admin";
import { CLIENT_ADMINS_CARD_COPY as C } from "../../lib/client-admin-copy";
import { defaultAdminRole, scopeSummaryParts } from "../../lib/client-access-draft-v2";
import { useAuthStore } from "../../stores/auth-store";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { AdminGrantModal, type AdminFormValue } from "../portal/AdminGrantModal";

interface Props {
  clientId: string;
}

export function ClientAdminsCard({ clientId }: Props): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const canManage =
    user?.permissions["clients.edit_all"] === true ||
    user?.permissions["projects.invite_client"] === true ||
    user?.permissions["kpi.manage"] === true;

  const [admins, setAdmins] = useState<ClientAdminRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editRow, setEditRow] = useState<ClientAdminRow | null>(null);
  // Kapsam seçicileri için set kategorileri + işletmeler (best-effort: izin yoksa/alınamazsa kapsam
  // daraltılamaz, Admin tam kapsamla atanır).
  const [sets, setSets] = useState<ClientAdminSet[]>([]);
  const [entities, setEntities] = useState<ClientEntity[]>([]);
  const [scopeStatus, setScopeStatus] = useState<"loading" | "ready" | "failed">("loading");
  const [scopeReload, setScopeReload] = useState(0);
  const [removeTarget, setRemoveTarget] = useState<ClientAdminRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!canManage) return;
    let aborted = false;
    listClientAdmins(clientId)
      .then((rows) => {
        if (!aborted) {
          setAdmins(rows);
          setError(null);
        }
      })
      .catch(() => {
        if (!aborted) setError(C.loadError);
      });
    return () => {
      aborted = true;
    };
  }, [clientId, canManage]);

  useEffect(() => {
    if (!canManage) return;
    let aborted = false;
    listClientKpiUsers(clientId)
      .then((res) => {
        if (aborted) return;
        setSets(res.sets);
        setEntities(res.entities);
        setScopeStatus("ready");
      })
      .catch(() => {
        if (!aborted) setScopeStatus("failed");
      });
    return () => {
      aborted = true;
    };
  }, [clientId, canManage, scopeReload]);

  if (!canManage) return null;

  async function reloadAdmins(): Promise<void> {
    setAdmins(await listClientAdmins(clientId));
  }

  async function handleSubmit(v: AdminFormValue): Promise<void> {
    if (editRow) {
      await updateClientAdmin(clientId, editRow.userId, {
        role: v.role,
        ...(v.role === "admin" && v.scope !== undefined ? { scope: v.scope } : {}),
      });
      setEditRow(null);
      setNotice(null);
    } else {
      const res = await grantClientAdmin(clientId, {
        email: v.email,
        fullName: v.fullName,
        role: v.role,
        ...(v.role === "admin" && v.scope !== undefined ? { scope: v.scope } : {}),
        grantMatchingAccess: v.grantMatching,
      });
      setNotice(res.mailSent ? null : C.modal.mailWarning);
      setInviteOpen(false);
    }
    await reloadAdmins();
  }

  async function handleRemove(): Promise<void> {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await revokeClientAdmin(clientId, removeTarget.userId);
      setAdmins((prev) => prev?.filter((a) => a.userId !== removeTarget.userId) ?? null);
      setRemoveTarget(null);
    } catch {
      setError(C.loadError);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div
      className="rounded-xl p-5"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-center justify-between mb-1">
        <h3
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.95rem",
            color: "var(--color-text)",
          }}
        >
          {C.title}
        </h3>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="text-xs"
          style={{ color: "var(--color-primary)", fontWeight: 600 }}
        >
          + {C.invite}
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--color-text-3)" }}>
        {C.hint}
      </p>
      {error ? (
        <div className="text-xs" style={{ color: "var(--color-error)" }}>
          {error}
        </div>
      ) : admins === null ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          …
        </div>
      ) : admins.length === 0 ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.empty}
        </div>
      ) : (
        <ul className="grid gap-2">
          {admins.map((a) => (
            <li key={a.userId} className="flex items-start justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div style={{ color: "var(--color-text)", fontWeight: 500 }}>
                  {a.fullName}
                  <span
                    className="ml-2 px-2 py-0.5 rounded-full"
                    style={{
                      fontSize: "0.65rem",
                      background: "color-mix(in srgb, var(--color-primary) 10%, white)",
                      color: "var(--color-primary)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {CLIENT_ADMIN_ROLE_LABELS[a.role]}
                  </span>
                  {!a.activated ? (
                    <span className="ml-2 text-xs" style={{ color: "var(--color-warning-text)" }}>
                      {C.pending}
                    </span>
                  ) : !a.isActive ? (
                    <span className="ml-2 text-xs" style={{ color: "var(--color-text-3)" }}>
                      {C.closed}
                    </span>
                  ) : null}
                </div>
                <div
                  className="text-xs truncate"
                  style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}
                >
                  {a.email}
                </div>
                <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
                  {a.role === "super_admin" ? C.fullScope : scopeLine(a, sets, entities)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditRow(a)}
                className="text-xs shrink-0"
                style={{ color: "var(--color-primary)", fontWeight: 600 }}
              >
                {C.editRole}
              </button>
              <button
                type="button"
                onClick={() => setRemoveTarget(a)}
                className="text-xs shrink-0"
                style={{ color: "var(--color-error)" }}
              >
                {C.remove}
              </button>
            </li>
          ))}
        </ul>
      )}
      {notice ? (
        <div
          role="status"
          className="mt-3 rounded-md px-3 py-2 text-xs"
          style={{
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
            border: "1px solid var(--color-warning-border)",
          }}
        >
          {notice}
        </div>
      ) : null}

      <AdminGrantModal
        open={inviteOpen || editRow !== null}
        row={editRow ?? undefined}
        sets={sets}
        entities={entities}
        scopeStatus={scopeStatus}
        onRetryScope={() => {
          setScopeStatus("loading");
          setScopeReload((n) => n + 1);
        }}
        warnLastSuper={editRow !== null && admins !== null && isLastActiveSuper(editRow, admins)}
        defaultRole={admins === null ? "admin" : defaultAdminRole(admins)}
        onClose={() => {
          setInviteOpen(false);
          setEditRow(null);
        }}
        onSubmit={handleSubmit}
      />
      <ConfirmDialog
        open={removeTarget !== null}
        title={C.removeDialog.title}
        body={`${C.removeDialog.body(removeTarget?.fullName ?? "")}${
          removeTarget && admins && isLastActiveSuper(removeTarget, admins)
            ? ` ${C.noSuperWarn}.`
            : ""
        }`}
        confirmLabel={removing ? C.removing : C.removeDialog.confirm}
        tone="danger"
        busy={removing}
        onConfirm={() => void handleRemove()}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}

function isLastActiveSuper(target: ClientAdminRow, all: readonly ClientAdminRow[]): boolean {
  return (
    target.role === "super_admin" &&
    target.isActive &&
    all.filter((a) => a.role === "super_admin" && a.isActive).length <= 1
  );
}

function scopeLine(a: ClientAdminRow, sets: ClientAdminSet[], entities: ClientEntity[]): string {
  const p = scopeSummaryParts(a.scope, sets, entities);
  return `${p.cats} · ${p.ents}`;
}
